// signupFunnel.ts — the authenticated half of the signup funnel (#133).
//
// The `signup` event fires from AuthModal while the visitor is still
// anonymous (email confirmation pending), and analytics run cookieless
// (persistence: "memory"), so that anonymous id dies with the tab and the
// event can never be joined to the person who later confirms. This module
// adds the events that CAN be joined, because they fire after
// useAuth's identifyUser():
//
//   email_confirmed   — the first authenticated load after /auth/callback
//   signup_completed  — same moment, once per user per device, carrying the
//                       attribution the anonymous `signup` event saw
//
// AuthCallback marks the hand-off in sessionStorage (it navigates within the
// same tab, so the flag survives exactly that one hop); Dashboard consumes it.

import { capture } from "./analytics";

export const AUTH_CALLBACK_FLAG_KEY = "parcely.authCallbackLanding";
export const SIGNUP_ATTRIBUTION_KEY = "parcely.signupAttribution";
export const SIGNUP_COMPLETED_KEY_PREFIX = "parcely.signupCompleted:";
// The attribution is read in a different tab (the confirmation link's), so it
// lives in localStorage; it is dropped if nobody confirms within a week.
const ATTRIBUTION_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface SignupAttribution {
  source: string;
  /** Only an explicit county the visitor arrived for (county landing page
   *  CTA). Never the default/last-viewed county: the old `county_selected`
   *  property read that and reported a county most users never chose. */
  landingCounty: string | null;
  intent: string | null;
}

export function markAuthCallbackLanding(): void {
  try {
    sessionStorage.setItem(AUTH_CALLBACK_FLAG_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** True once, on the first authenticated load after /auth/callback. */
export function consumeAuthCallbackLanding(): boolean {
  try {
    if (sessionStorage.getItem(AUTH_CALLBACK_FLAG_KEY) !== "1") return false;
    sessionStorage.removeItem(AUTH_CALLBACK_FLAG_KEY);
    return true;
  } catch {
    return false;
  }
}

export function saveSignupAttribution(attr: SignupAttribution, now: number = Date.now()): void {
  try {
    localStorage.setItem(SIGNUP_ATTRIBUTION_KEY, JSON.stringify({ ...attr, savedAt: now }));
  } catch {
    /* ignore */
  }
}

export function takeSignupAttribution(now: number = Date.now()): SignupAttribution | null {
  try {
    const raw = localStorage.getItem(SIGNUP_ATTRIBUTION_KEY);
    if (!raw) return null;
    localStorage.removeItem(SIGNUP_ATTRIBUTION_KEY);
    const parsed = JSON.parse(raw) as Partial<SignupAttribution> & { savedAt?: number };
    if (typeof parsed.savedAt !== "number" || now - parsed.savedAt > ATTRIBUTION_TTL_MS) return null;
    return {
      source: typeof parsed.source === "string" ? parsed.source : "unknown",
      landingCounty: typeof parsed.landingCounty === "string" ? parsed.landingCounty : null,
      intent: typeof parsed.intent === "string" ? parsed.intent : null,
    };
  } catch {
    return null;
  }
}

/**
 * Fire the authenticated funnel events for a load that came through
 * /auth/callback. Call only after the user is signed in (identify has run).
 * Returns true when the events fired.
 */
export function trackConfirmedSignupLanding(userId: string): boolean {
  if (!consumeAuthCallbackLanding()) return false;
  capture("email_confirmed");
  const key = SIGNUP_COMPLETED_KEY_PREFIX + userId;
  let alreadySent = false;
  try {
    alreadySent = localStorage.getItem(key) === "1";
  } catch {
    /* ignore */
  }
  if (!alreadySent) {
    const attr = takeSignupAttribution();
    capture("signup_completed", {
      source: attr?.source ?? "unknown",
      landing_county: attr?.landingCounty ?? null,
      intent: attr?.intent ?? null,
    });
    try {
      localStorage.setItem(key, "1");
    } catch {
      /* ignore */
    }
  }
  return true;
}
